import { CANONICAL_ORIGIN, SEO_DEFAULTS, SITE } from "@/config/site.config";

export const ORG_ID = `${CANONICAL_ORIGIN}/#organization`;
export const WEBSITE_ID = `${CANONICAL_ORIGIN}/#website`;

export const DEFAULT_TITLE = SEO_DEFAULTS.defaultTitle;
export const DEFAULT_DESC = SEO_DEFAULTS.defaultDescription;
// Absolute URL is mandatory — every platform rejects a relative og:image and shows
// no preview image at all.
export const DEFAULT_IMAGE = `${CANONICAL_ORIGIN}${SITE.ogImagePath}`;

/** Trim to a search-result-friendly length, breaking on a word boundary. */
export const truncate = (text = "", max = 155) => {
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
};

/**
 * Absolute URL for a route path, including any deployment base path, with no trailing
 * slash except at the root. Canonical tags, Open Graph URLs, the sitemap and every
 * schema block route through here, so the URL shape is decided in exactly one place.
 */
export const absoluteUrl = (path = "/") => {
  const clean = path === "/" ? "/" : `/${String(path).replace(/^\/+|\/+$/g, "")}`;
  return `${CANONICAL_ORIGIN}${clean}`;
};

/** BreadcrumbList schema from `[{ name, path }]`, in order from home to current page. */
export const breadcrumbSchema = (crumbs = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: absoluteUrl(c.path),
  })),
});

/** FAQPage schema from `[{ q, a }]`. Only emit this where the questions are visible on the page. */
export const faqSchema = (faq = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

/** Service schema for a service detail page. */
export const serviceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.name,
  serviceType: service.name,
  description: service.summary,
  url: absoluteUrl(`/services/${service.slug}`),
  provider: { "@id": ORG_ID },
  areaServed: { "@type": "Country", name: "India" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: `${service.name} deliverables`,
    itemListElement: (service.deliverables || []).map((d) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: d },
    })),
  },
});

/** CreativeWork schema for a case study. */
export const caseStudySchema = (study) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name: study.title,
  headline: study.title,
  description: study.summary,
  url: absoluteUrl(`/work/${study.slug}`),
  creator: { "@id": ORG_ID },
  about: study.client,
  dateCreated: study.year,
  keywords: (study.services || []).join(", "),
});

/** BlogPosting schema for an article. */
export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.excerpt,
  url: absoluteUrl(`/blog/${post.slug}`),
  datePublished: post.date,
  dateModified: post.updated || post.date,
  author: { "@type": "Organization", name: post.author || "Snapx Technologies", "@id": ORG_ID },
  publisher: { "@id": ORG_ID },
  mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl(`/blog/${post.slug}`) },
  keywords: (post.tags || []).join(", "),
});
